import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import api from '../../services/api';
import { getReviews, submitReview } from '../../services/reviews';
import { useAuth } from '../../context/AuthContext';
import { useToast } from '../../components/ui/Modal';
import { Globe, Users, Star, Clock, FileText, BookOpen, ClipboardList, Check } from 'lucide-react';

export default function CourseDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [course, setCourse] = useState(null);
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [enrolling, setEnrolling] = useState(false);
  const [reviewForm, setReviewForm] = useState({ rating: 5, comment: '' });
  const [tab, setTab] = useState('overview');
  const toast = useToast();

  useEffect(() => {
    Promise.all([
      api.get(`/courses/${id}`),
      getReviews(id).catch(() => ({ data: { data: [] } }))
    ]).then(([c, r]) => {
      setCourse(c.data);
      setReviews(r.data?.data || r.data || []);
    }).catch(() => {}).finally(() => setLoading(false));
  }, [id]);

  const handleEnroll = async () => {
    setEnrolling(true);
    try {
      await api.post(`/courses/${id}/enroll`);
      navigate(`/learn/${id}`);
    } catch(e) {
      toast.error(e.response?.data?.message || 'Ro\'yxatdan o\'tishda xatolik');
    } finally { setEnrolling(false); }
  };

  const handleReview = async (e) => {
    e.preventDefault();
    try {
      const r = await submitReview(id, reviewForm);
      setReviews(prev => [r.data, ...prev.filter(x => x.user_id !== user.id)]);
      setReviewForm({ rating: 5, comment: '' });
    } catch(e) { toast.error('Sharh yuborishda xatolik'); }
  };

  const diffLabels = { beginner: "Boshlang'ich", intermediate: "O'rta", advanced: "Yuqori" };

  if (loading || !course) return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-8 w-8 border-2 border-blue-500 border-t-transparent"></div></div>;

  return (
    <div className="max-w-5xl mx-auto space-y-6">
      {/* Hero */}
      <div className="bg-gradient-to-r from-blue-600 to-purple-600 rounded-2xl p-8 text-white">
        <div className="flex items-start justify-between">
          <div className="flex-1">
            <div className="flex items-center gap-3 mb-3">
              {course.difficulty_level && <span className="text-xs bg-white/20 px-3 py-1 rounded-full">{diffLabels[course.difficulty_level]}</span>}
              {course.language && <span className="text-xs bg-white/20 px-3 py-1 rounded-full flex items-center gap-1"><Globe className="w-3 h-3" /> {course.language?.toUpperCase()}</span>}
            </div>
            <h1 className="text-3xl font-bold mb-3">{course.title}</h1>
            <p className="text-blue-100 mb-4">{course.description}</p>
            <div className="flex items-center gap-6 text-sm text-blue-200">
              <span className="flex items-center gap-1"><Users className="w-4 h-4" /> {course.teacher?.name}</span>
              {course.rating > 0 && <span className="flex items-center gap-1"><Star className="w-4 h-4" /> {course.rating}</span>}
              <span className="flex items-center gap-1"><Users className="w-4 h-4" /> {course.students_count || 0} talaba</span>
              {course.estimated_hours && <span className="flex items-center gap-1"><Clock className="w-4 h-4" /> {course.estimated_hours} soat</span>}
            </div>
          </div>
          <div className="ml-8">
            {user?.role === 'student' && (
              <button onClick={handleEnroll} disabled={enrolling} className="px-8 py-3 bg-white text-blue-600 rounded-xl font-bold hover:bg-blue-50 transition disabled:opacity-50">
                {enrolling ? 'Yuklanmoqda...' : 'Yozilish'}
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
        <div className="flex border-b border-gray-100">
          {['overview', 'content', 'reviews'].map(t => (
            <button key={t} onClick={() => setTab(t)} className={`px-6 py-3 text-sm font-medium capitalize ${tab === t ? 'text-blue-600 border-b-2 border-blue-600' : 'text-gray-500'}`}>
              {t === 'overview' ? 'Umumiy' : t === 'content' ? 'Tarkib' : 'Sharhlar'}
            </button>
          ))}
        </div>

        <div className="p-6">
          {tab === 'overview' && (
            <div className="space-y-6">
              {course.objectives?.length > 0 && (
                <div><h3 className="font-bold text-gray-900 mb-2">Kurs maqsadlari</h3>
                  <ul className="space-y-2">{course.objectives.map((o, i) => <li key={i} className="flex items-start gap-2 text-sm text-gray-600"><Check className="w-4 h-4 text-green-500 flex-shrink-0 mt-0.5" />{o}</li>)}</ul>
                </div>
              )}
              {course.requirements && <div><h3 className="font-bold text-gray-900 mb-2">Talablar</h3><p className="text-sm text-gray-600">{course.requirements}</p></div>}
            </div>
          )}
          {tab === 'content' && (
            <div className="space-y-4">
              {course.modules?.map((m, mi) => (
                <div key={m.id} className="border border-gray-100 rounded-xl overflow-hidden">
                  <div className="bg-gray-50 px-5 py-3 font-semibold text-gray-900">{mi + 1}. {m.title}</div>
                  <div className="divide-y divide-gray-100">
                    {m.lessons?.map((l, li) => (
                      <div key={l.id} className="px-5 py-3 flex items-center justify-between">
                        <span className="text-sm text-gray-600">{mi + 1}.{li + 1} {l.title}</span>
                        <span className="text-xs text-gray-400">{l.type} · {l.duration_minutes || '?'} min</span>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
              {(!course.modules || course.modules.length === 0) && <p className="text-gray-500 text-center py-8">Hali tarkib qo'shilmagan</p>}
            </div>
          )}
          {tab === 'reviews' && (
            <div className="space-y-6">
              {user?.role === 'student' && (
                <form onSubmit={handleReview} className="bg-gray-50 rounded-xl p-4 space-y-3">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium text-gray-700">Bahoingiz:</span>
                    {[1,2,3,4,5].map(s => (
                      <button key={s} type="button" onClick={() => setReviewForm({...reviewForm, rating: s})} className={`text-2xl ${s <= reviewForm.rating ? 'text-yellow-400' : 'text-gray-300'}`}>★</button>
                    ))}
                  </div>
                  <textarea value={reviewForm.comment} onChange={e => setReviewForm({...reviewForm, comment: e.target.value})} placeholder="Sharh yozing..." rows={2} className="w-full px-4 py-2 rounded-xl border border-gray-200 text-sm outline-none focus:border-blue-500" />
                  <button type="submit" className="px-5 py-2 bg-blue-600 text-white rounded-xl text-sm font-medium hover:bg-blue-700">Yuborish</button>
                </form>
              )}
              {reviews.map(r => (
                <div key={r.id} className="flex gap-4">
                  <div className="w-9 h-9 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center text-sm font-bold flex-shrink-0">{r.user?.name?.charAt(0)}</div>
                  <div>
                    <div className="flex items-center gap-2 mb-1">
                      <span className="font-medium text-gray-900 text-sm">{r.user?.name}</span>
                      <span className="text-yellow-500 text-xs">{'★'.repeat(r.rating)}</span>
                    </div>
                    {r.comment && <p className="text-sm text-gray-600">{r.comment}</p>}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}